
import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Creature,
  CreatureGift,
  SpellcastingEntry,
} from "@dndModels";
import { 
  CHARACTER_LEVELS,
  CR_OPTIONS, 
  CREATURE_CRS
} from "@dndModels/Constants";

import { makeStyles } from "@utilities/theme_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import StringBox from "@components/input/strings/StringBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import SelectSpellIndex from "@dndComponents/select/SelectSpellIndex";
import CreatureSpell from "@dndComponents/creature/CreatureSpell";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import CopyButton from "@components/input/buttons/icon_buttons/CopyButton";
import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import CheckBox from "@components/input/CheckBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import DescriptionBox from "@dndComponents/DescriptionBox";

interface Props {
  obj: Creature | CreatureGift;
  disabled: boolean;
  onChange: () => void;
}

export default function CreatureSpells({
  obj,
  disabled,
  onChange
}: Props) {
  const [spellcasting_cr, setSpellcastingCR] = useState(obj.spellcasting_table.entries.length > 0 ? Math.min(...obj.spellcasting_table.entries.map(o => o.level)) : -1);
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [spells_mode, setSpellsMode] = useState("Add Spells");
  const [display_mode, setDisplayMode] = useState("Spells");
  const [add_at_level, setAddAtLevel] = useState("Next");
  const [trickle_up, setTrickleUp] = useState(false);
  const [shift_by_level, setShiftByLevel] = useState(0);
  
  const useRowStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        color: "white"
      },
    });
  });

  useEffect(() => {
    if(reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const renderSpells = () => {
    const spellcasting_entry = obj.spellcasting_table.entries.find(o => o.level === spellcasting_cr);
    const spell_levels = spellcasting_entry ? spellcasting_entry.spell_levels : [];
    return (
      <Grid container spacing={0} direction="row">
        { spellcasting_entry && spell_levels.map(level => {
          return (
            <Grid
              key={level}
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              <Grid
                size={{
                  xs: 12
                }}>{ level === 0 ? "Cantrips" : `Spell Level: ${level}` }</Grid>
              { spellcasting_entry.spells.filter(o => o.spell_level === level).map(spell => {
                // console.log(spell);
                return (
                  <Grid
                    key={spell.spell_id}
                    size={{
                      xs: 12
                    }}>
                    <CreatureSpell
                      obj={obj}
                      spellcasting_cr={spellcasting_cr}
                      spell={spell}
                      disabled={disabled}
                      onChange={onChange}
                      onDelete={() => {
                        spellcasting_entry.spells = spellcasting_entry.spells.filter(o => o.spell_id !== spell.spell_id);
                        onChange();
                        setPing(!ping);
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          );
        })}
      </Grid>
    );
  }

  const renderSC = () => {
    const spellcasting_entry = obj.spellcasting_table.getEntry(spellcasting_cr);
    if (spellcasting_entry) {
      // console.log(spellcasting_entry);
      return (
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <DescriptionBox 
              name="Spellcasting Description"
              value={spellcasting_entry.description}
              onChange={(changed: string) => {
                spellcasting_entry.description = changed;
                onChange();
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <CenteredMenu
              options={["Add Spells","Manage Casting"]}
              selected={spells_mode}
              onSelect={setSpellsMode}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            { spells_mode === "Add Spells" ? 
              <SelectSpellIndex
                spell_ids={spellcasting_entry.spell_ids}
                disabled={disabled}
                onChangeByLevel={(changed: { [level: number]: string[] }, mapping: { [old_id: string]: string }) => {
                  // const new_obj = obj;
                  spellcasting_entry.setSpells(changed, mapping);
                  onChange();
                  setPing(!ping);
                }} 
              />
            : renderSpells() }
          </Grid>
        </>
      );
    }
    return null;
  }
  
  const { classes: rowClasses, cx: cx2 } = useRowStyles();
  
  const cr_options = obj instanceof Creature ? CR_OPTIONS.filter(o => 
    +o >= obj.min_challenge_rating && +o <= obj.max_challenge_rating 
  ) : CHARACTER_LEVELS;
  const used_levels = obj.spellcasting_table.entries.map(o => o.level);

  const renderTable = () => {
    let level_options: number[] = [];
    if (obj instanceof Creature) {
      level_options = CR_OPTIONS.map(o => +o).filter(o => 
        o >= obj.min_challenge_rating && 
        o <= obj.max_challenge_rating
      );
    } else {
      level_options = CHARACTER_LEVELS.map(o => +o).filter(o => o < obj.max_level);
    }
    // const min = level_options.length === 0 ? 0 : Math.min(...level_options);
    // const max = level_options.length === 0 ? 0 : Math.max(...level_options);
    // const empty_entries = level_options.map(o => +o).filter(o => o > min && o < max);
    return (
      <>
        <Grid
          className="header"
          size={{
            xs: 4
          }}>
          Entries&nbsp;
          <AddButton
            disabled={disabled}
            onClick={() => {
              // debugger;
              const new_obj = obj;
              const entry = new SpellcastingEntry();
              if (add_at_level === "Next") {
                const max_level = Math.max(...new_obj.spellcasting_table.entries.map(o => o.level), 0);
                entry.level = 1;
                const previous = new_obj.spellcasting_table.entries.find(o => o.level === max_level);
                if (previous) {
                  if (previous.level > 0.5) { // It's going to be a whole number, so just increment
                    entry.level = previous.level + 1;
                  } else {
                    const level_options = (new_obj instanceof Creature ? [...CREATURE_CRS] : [...CHARACTER_LEVELS]).map(o => +o).filter(o => o > previous.level);
                    entry.level = Math.min(...level_options);
                  }
                  entry.spells = previous.spells.map(o => { return {...o}; });
                } 
              } else {
                entry.level = +add_at_level;
                const max_level = Math.max(...new_obj.spellcasting_table.entries.filter(o => o.level < entry.level).map(o => o.level), 0);
                const previous = new_obj.spellcasting_table.entries.find(o => o.level === max_level);
                if (previous) {
                  entry.spells = previous.spells.map(o => { return {...o}; });
                } else {
                  // It's a new lowest.  Make it match the previous lowest, and remove trickle up.
                  const min_level = new_obj.spellcasting_table.entries.length === 0 ? 0 : Math.min(...new_obj.spellcasting_table.entries.map(o => o.level));
                  const lowest = new_obj.spellcasting_table.entries.find(o => o.level === min_level);
                  if (lowest) {
                    entry.spells = lowest.spells.map(o => { return {...o}; });
                    setTrickleUp(false);
                  } 
                }
                setAddAtLevel("Next");
              }
              new_obj.spellcasting_table.entries.push(entry);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <SelectStringBox
            name="Add at Level"
            options={level_options.filter(o => !used_levels.includes(o)).map(o => { return `${o}`; })}
            extra_options={["Next"]}
            value={add_at_level}
            disabled={disabled}
            onChange={setAddAtLevel}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <CheckBox
            name="Trickle Up"
            value={trickle_up}
            disabled={disabled}
            onChange={setTrickleUp}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <StringBox
            name="Shift by Level"
            value={`${shift_by_level}`}
            disabled={disabled}
            onBlur={(changed: string) => {
              setShiftByLevel(+changed);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <ButtonBox
            name="Shift Up"
            disabled={disabled || shift_by_level === 0}
            onClick={() => {
              const new_obj = obj;
              for (const entry of new_obj.spellcasting_table.entries) {
                entry.level += shift_by_level;
              }
              onChange();
              setShiftByLevel(0);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <ButtonBox
            name="Shift Down"
            disabled={disabled || shift_by_level === 0}
            onClick={() => {
              const new_obj = obj;
              for (const entry of new_obj.spellcasting_table.entries) {
                entry.level -= shift_by_level;
              }
              onChange();
              setShiftByLevel(0);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          {/* <ButtonBox
            name="Extrapolate Empty Entries"
            disabled={disabled || empty_entries.length === 0}
            onClick={() => {
              const new_obj = obj;
              new_obj.spellcasting_table.extrapolateEmptyFields();
              onChange();
              setPing(!ping);
            }}
          /> */}
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row"
            className={cx2(rowClasses.root)}>
            <Grid
              className='centered'
              size={{
                xs: 4
              }}>
              Level
            </Grid>
            <Grid
              className='centered'
              size={{
                xs: 4
              }}>
              Spells
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
            </Grid>
          </Grid>
        </Grid>
        { obj.spellcasting_table.entries.sort((a, b) => { return a.level - b.level; }).map((entry, key) => {
          let options: string[] = []; 
          if (obj instanceof Creature) {
            options = CR_OPTIONS.map(o => +o).filter(o => 
              o >= obj.min_challenge_rating && 
              o <= obj.max_challenge_rating &&
              (o === entry.level || !used_levels.includes(o))
            ).map(o => { return `${o}`; });
          } else {
            options = CHARACTER_LEVELS.map(o => +o).filter(o => 
              o === entry.level || !used_levels.includes(o)).map(o => { return `${o}`; });
          }
          return (
            <Grid
              key={key}
              className="padded"
              size={{
                xs: 12
              }}>
              <Grid container spacing={0} direction="row"
                className={cx2(rowClasses.root)}>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <SelectStringBox
                    name=""
                    options={options}
                    value={ `${entry.level}` }
                    onChange={(changed: string) => {
                      const new_obj = obj;
                      const new_cr = +changed;
                      const old_entry = new_obj.spellcasting_table.entries.find(o => o.level === entry.level);
                      if (old_entry) {
                        old_entry.level = new_cr;
                        onChange();
                      }
                      setSpellcastingCR(new_cr);
                      setReloading(true);
                    }}
                  />
                </Grid>
                <Grid
                  className="clickable centered"
                  onClick={() => {
                    setSpellcastingCR(entry.level);
                    setDisplayMode("Spells");
                  }}
                  size={{
                    xs: 4
                  }}>
                  { entry.spells.length }
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <CopyButton
                    onClick={() => {
                      const new_obj = obj;
                      const new_entry = entry.clone();
                      if (add_at_level === "Next") {
                        new_entry.level++;
                      } else {
                        new_entry.level = +add_at_level;
                      }
                      new_obj.spellcasting_table.entries.push(new_entry);
                      onChange();
                      setReloading(true);
                    }}
                  />
                  &nbsp;
                  <DeleteButton
                    onClick={() => {
                      const new_obj = obj;
                      new_obj.spellcasting_table.entries = new_obj.spellcasting_table.entries.filter(o => o.level !== entry.level);
                      onChange();
                      setReloading(true);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        })}
        <Grid
          size={{
            xs: 12
          }}></Grid>
      </>
    );
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 12
        }}>
        <CenteredMenu
          options={["Spells","Table"]}
          selected={display_mode}
          onSelect={setDisplayMode}
        />
      </Grid>
      { display_mode === "Spells" ? 
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox 
              name="Select CR for Spellcasting"
              options={cr_options}
              value={`${spellcasting_cr}`}
              onChange={(changed: string) => {
                const new_obj = obj;
                const new_cr = +changed;
                const old_entry = new_obj.spellcasting_table.getEntry(new_cr);
                if (!old_entry || old_entry.level !== new_cr) {
                  const entry = old_entry ? old_entry.clone() : new SpellcastingEntry();
                  entry.level = new_cr;
                  new_obj.spellcasting_table.entries.push(entry);
                  onChange();
                }
                setSpellcastingCR(new_cr);
                setReloading(true);
              }}
            />
          </Grid>
          { renderSC() }
        </>
      : renderTable() }
    </Grid>
  ); 
}
